(() => {
const PRODUCT_DISCOVERY_RELEASE_ID = '365nails-product-discovery-v1';
const PRODUCT_DISCOVERY_DOUBLE_LOAD_SAFE = 'product-discovery-double-load-safe-v1';
const PRODUCT_DISCOVERY_RELATED_GRID = 'product-discovery-related-grid-v1';
const PRODUCT_DISCOVERY_COMPLEMENTARY_SLIDER_SAFE =
  'product-discovery-complementary-card-parity-v5';
const PRODUCT_CARD_DISPLAY_TITLE_LIMIT = 72;
const PRODUCT_RECOMMENDATION_SEQUENCE = [
  'proven',
  'distinctive',
  'rising',
  'new',
  'proven',
  'explore',
  'distinctive',
  'proven',
];

function productDiscoveryHash(value) {
  let hash = 2166136261;
  for (const character of String(value || '')) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function productDiscoveryStyle(value) {
  const text = String(value || '').trim().toLowerCase();
  const buckets = [
    ['french', /french/u],
    ['floral', /floral|flower|rose|bloom/u],
    ['crystal', /crystal|rhinestone|diamond|bling/u],
    ['chrome', /chrome|metallic|mirror/u],
    ['cat-eye', /cat.?eye|magnetic/u],
    ['solid', /solid|minimal/u],
    ['art', /paint|art|sculpt|3d|emboss/u],
  ];
  return buckets.find(([, pattern]) => pattern.test(text))?.[0] || 'other';
}

function recommendationCandidate(card, index, total) {
  const link = card.querySelector('[data-product-handle], a[href*="/products/"]');
  const handle = link?.dataset.productHandle || link?.getAttribute('href') || `position-${index}`;
  const title = card.querySelector('[data-product-card-title-full]')?.dataset.productCardTitleFull
    || card.querySelector('.product-card__title')?.textContent
    || handle;
  const badges = [...card.querySelectorAll('.f-badge')]
    .map((item) => item.textContent)
    .join(' ')
    .toLowerCase();
  return {
    card,
    style: productDiscoveryStyle(title),
    relevance: total <= 1 ? 100 : Math.round((1 - index / (total - 1)) * 100),
    isNew: /\bnew\b/u.test(badges),
    isRising: /sale|hot|best/u.test(badges),
    discovery: productDiscoveryHash(handle) % 101,
    originalIndex: index,
  };
}

function recommendationLaneScore(candidate, lane, styleCounts) {
  if (lane === 'proven') return candidate.relevance;
  if (lane === 'new') return (candidate.isNew ? 70 : 0) + candidate.relevance * 0.3;
  if (lane === 'rising') return (candidate.isRising ? 55 : 0) + candidate.relevance * 0.45;
  if (lane === 'distinctive') {
    const rarity = 100 / Math.max(1, styleCounts.get(candidate.style) || 1);
    return rarity * 0.65 + candidate.discovery * 0.35;
  }
  return candidate.discovery * 0.65 + (100 - candidate.relevance) * 0.35;
}

function reorderRecommendationCards(container) {
  const list = container.querySelector('.products, .products-grid, .products-list, .f-grid, ul');
  if (!list || list.dataset.productDiscoveryRelease === PRODUCT_DISCOVERY_RELEASE_ID) return 0;
  if (list.closest('.swiper-initialized') || list.querySelector('.swiper-slide-duplicate')) return 0;
  const cards = [...list.children].filter((item) => item.querySelector('.product-card'));
  if (cards.length < 2) return 0;
  const candidates = cards.map((card, index) => recommendationCandidate(card, index, cards.length));
  const styleCounts = candidates.reduce((counts, candidate) => {
    counts.set(candidate.style, (counts.get(candidate.style) || 0) + 1);
    return counts;
  }, new Map());
  const remaining = [...candidates];
  const ordered = [];
  for (const lane of PRODUCT_RECOMMENDATION_SEQUENCE.slice(0, candidates.length)) {
    const ranked = remaining
      .map((candidate) => ({
        candidate,
        score: recommendationLaneScore(candidate, lane, styleCounts),
      }))
      .sort((left, right) => right.score - left.score
        || left.candidate.originalIndex - right.candidate.originalIndex);
    const recentStyles = ordered.slice(-2).map(({ candidate }) => candidate.style);
    let choice = ranked.find(({ candidate }) =>
      recentStyles.length < 2
      || !recentStyles.every((style) => style === candidate.style));
    choice ||= ranked[0];
    if (!choice) break;
    ordered.push({ ...choice, lane });
    remaining.splice(remaining.indexOf(choice.candidate), 1);
  }
  for (const [index, row] of ordered.entries()) {
    row.candidate.card.dataset.productDiscoveryLane = row.lane;
    row.candidate.card.dataset.productDiscoveryOriginalPosition = String(
      row.candidate.originalIndex + 1,
    );
    list.append(row.candidate.card);
    row.candidate.card.style.setProperty('--product-discovery-position', index + 1);
  }
  list.dataset.productDiscoveryRelease = PRODUCT_DISCOVERY_RELEASE_ID;
  return ordered.length;
}

function recommendationElement(responseText) {
  return new DOMParser()
    .parseFromString(responseText, 'text/html')
    .querySelector('.shopify-section product-recommendations');
}

function constrainComplementarySlider(container) {
  if (!container.classList.contains('complementary-products')) return;
  const block = container.closest('.product__block');
  block?.style.setProperty('min-width', '0');
  block?.style.setProperty('max-width', '100%');
  container.style.setProperty('display', 'block');
  container.style.setProperty('min-width', '0');
  container.style.setProperty('width', '100%');
  container.style.setProperty('max-width', '100%');
}

function productCardDisplayTitle(value) {
  const normalized = String(value || '')
    .replace(/\s+/gu, ' ')
    .replace(/\b365nails:\s*/giu, '')
    .replace(/\s*[|｜]\s*365nails\s*$/iu, '')
    .trim();
  if (normalized.length <= PRODUCT_CARD_DISPLAY_TITLE_LIMIT) return normalized;
  return `${normalized.slice(0, PRODUCT_CARD_DISPLAY_TITLE_LIMIT - 3).trimEnd()}...`;
}

function alignComplementaryProductCards(container) {
  if (!container.classList.contains('complementary-products')) return 0;
  const cards = [...container.querySelectorAll('.product-card')];
  for (const card of cards) {
    card.classList.remove('product-card--simple');
    card.classList.add('product-card-style-card');
    card.dataset.dsComponent = 'product-card';
    card.dataset.productCardPresentation = PRODUCT_DISCOVERY_COMPLEMENTARY_SLIDER_SAFE;
    card.querySelector('.product-card__wrapper')?.classList.add('h-full');
    card
      .querySelector('.product-card__image-wrapper')
      ?.classList.add('color-scheme-1', 'bg-none');

    const titleLink = card.querySelector('.product-card__title a[href*="/products/"]');
    if (!titleLink) continue;
    const fullTitle = String(
      titleLink.dataset.productCardTitleFull
        || titleLink.getAttribute('aria-label')
        || titleLink.textContent
        || '',
    ).replace(/\s+/gu, ' ').trim();
    const displayTitle = productCardDisplayTitle(fullTitle);
    if (!fullTitle || !displayTitle) continue;
    titleLink.classList.add('block', 'text-limit-1-line');
    titleLink.setAttribute('aria-label', fullTitle);
    titleLink.dataset.productCardDisplayTitle = 'p1-j';
    titleLink.dataset.productCardTitleFull = fullTitle;
    titleLink.dataset.productCardTitleShort = displayTitle;
    titleLink.dataset.productCardTitleChanged = String(displayTitle !== fullTitle);
    let label = titleLink.querySelector('.reversed-link__text');
    if (!label) {
      label = titleLink.ownerDocument.createElement('span');
      label.className = 'reversed-link__text';
      titleLink.replaceChildren(label);
    }
    if (label.textContent !== displayTitle) label.textContent = displayTitle;
  }
  return cards.length;
}

if (!customElements.get('product-recommendations')) {
  customElements.define(
    'product-recommendations',
    class ProductRecommendations extends HTMLElement {
      constructor() {
        super();

        if ('requestIdleCallback' in window) {
          requestIdleCallback(this.init.bind(this), { timeout: 1500 });
        } else {
          FoxTheme.Motion.inView(this, this.init.bind(this), { margin: '0px 0px 400px 0px' });
        }
      }

      async init() {
        try {
          constrainComplementarySlider(this);
          const response = await fetch(this.dataset.url);
          const recommendations = recommendationElement(await response.text());

          if (recommendations?.querySelector('.product-card')) {
            if (this.classList.contains('complementary-products')) {
              alignComplementaryProductCards(recommendations);
            } else {
              reorderRecommendationCards(recommendations);
            }
            this.innerHTML = recommendations.innerHTML;
            this.dispatchEvent(new CustomEvent('recommendations:loaded'));
            return;
          }

          if (this.classList.contains('complementary-products')) {
            this.closest('.product__block')?.remove();
          } else {
            this.closest('.shopify-section')?.remove();
          }
          this.dispatchEvent(new CustomEvent('recommendations:empty'));
        } catch (error) {
          console.error(error);
        }
      }
    }
  );
}
})();
